import { useState, useEffect, FormEvent, ReactNode } from 'react';
import { apiService } from '../services/api';

interface MFAEmailConfirmProps {
  emailMasked: string;
  expiresIn: number; // seconds until the current code expires
  onVerify: (code: string) => Promise<void>; // throws on failure; parent navigates on success
  onResend: () => Promise<number>; // returns the new expires_in
  onBack: () => void;
  backLabel?: string;
  title?: string;
  subtitle?: ReactNode;
}

// Email one-time-code entry with a live expiry countdown and resend (issue #213).
// Reused by both enrollment (MFA setup) and the login challenge.
export default function MFAEmailConfirm({
  emailMasked,
  expiresIn,
  onVerify,
  onResend,
  onBack,
  backLabel = 'Back',
  title = 'Email Verification',
  subtitle,
}: MFAEmailConfirmProps) {
  const [code, setCode] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(expiresIn);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Restart the countdown whenever a fresh code is issued (expiresIn changes).
  useEffect(() => {
    setSecondsLeft(expiresIn);
  }, [expiresIn]);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const timer = setInterval(() => setSecondsLeft((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(timer);
  }, [secondsLeft]);

  const expired = secondsLeft <= 0;
  const mm = Math.floor(secondsLeft / 60);
  const ss = String(secondsLeft % 60).padStart(2, '0');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    const trimmed = code.trim();
    if (trimmed.length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }
    if (expired) {
      setError('This code has expired. Request a new one.');
      return;
    }
    setIsLoading(true);
    try {
      await onVerify(trimmed);
    } catch (err) {
      setError(apiService.getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setCode('');
    setIsLoading(true);
    try {
      const newExpiry = await onResend();
      setSecondsLeft(newExpiry);
    } catch (err) {
      setError(apiService.getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-2">{title}</h2>
      <p className="text-gray-600 text-sm mb-6">
        {subtitle || (
          <>
            We sent a one-time code to <span className="font-medium">{emailMasked}</span>. Enter it below.
          </>
        )}
      </p>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">{error}</div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="email-code" className="block text-sm font-medium text-gray-700 mb-1">
            Email Code
          </label>
          <input
            id="email-code"
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-2xl tracking-widest"
            placeholder="000000"
            maxLength={6}
            autoComplete="one-time-code"
            inputMode="numeric"
            autoFocus
            disabled={isLoading}
          />
          <p className={`mt-2 text-sm ${expired ? 'text-red-600' : 'text-gray-500'}`}>
            {expired ? 'Code expired — request a new one' : `Code expires in ${mm}:${ss}`}
          </p>
        </div>

        <button
          type="submit"
          disabled={isLoading || code.length !== 6 || expired}
          className="w-full py-2 px-4 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Verifying...' : 'Verify'}
        </button>
      </form>

      <div className="mt-4 flex flex-col items-center gap-2">
        <button
          type="button"
          onClick={handleResend}
          disabled={isLoading}
          className="text-sm text-blue-600 hover:underline disabled:text-gray-400 disabled:no-underline disabled:cursor-not-allowed"
        >
          Resend code
        </button>
        <button type="button" onClick={onBack} className="text-sm text-gray-600 hover:underline">
          {backLabel}
        </button>
      </div>
    </div>
  );
}
